import type { MapDocument } from "@/src/types/map";
import { assignMissingObjectModes } from "@/src/core/mapMode";
import { t } from "@/src/i18n";
import { isWallObjectType } from "@/src/core/engine/wallObjects";
import { isFloorObjectType } from "@/src/core/engine/floorObjects";
import {
  LEGACY_STAMP_TO_MARKER,
  isMarkerObjectType,
  normaliseLegacyMarker,
} from "@/src/core/engine/markerObjects";
import { isTerrainPatchType } from "@/src/core/engine/terrainPatches";
import { isRoomKeyMap } from "@/src/core/roomKeys";
import { STAMP_CATALOG } from "@/src/core/engine/stampCatalog";

const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

const safeFilename = (title: string) =>
  title.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") ||
  "dungeon-map";

export const exportDocumentJson = (document: MapDocument) => {
  downloadBlob(
    new Blob([JSON.stringify(document, null, 2)], { type: "application/json" }),
    `${safeFilename(document.meta.title)}.json`,
  );
};

export const downloadPngBlob = (blob: Blob, title: string) =>
  downloadBlob(blob, `${safeFilename(title)}.png`);

const isNumberPair = (value: unknown): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  typeof value[0] === "number" &&
  Number.isFinite(value[0]) &&
  typeof value[1] === "number" &&
  Number.isFinite(value[1]);

export const parseMapDocument = (value: unknown): MapDocument => {
  if (!value || typeof value !== "object") throw new Error(t("validate.invalidFile"));

  const candidate = value as Partial<MapDocument>;
  const geometry = candidate.geometry?.floorPolygons;
  const hexCells = candidate.geometry?.hexCells;
  const ringsAreValid =
    Array.isArray(geometry) &&
    geometry.every((polygon) =>
      Array.isArray(polygon) &&
      polygon.every(
        (ring) => Array.isArray(ring) && ring.length >= 4 && ring.every(isNumberPair),
      ),
    );
  const terrainIds = new Set([
    "plains", "forest", "hills", "mountain", "water", "swamp", "desert", "snow", "settlement",
  ]);
  // Derived rather than hand-listed: everything the drawer can place, plus the
  // legacy ids Slice 3 absorbed (still accepted, then normalised below). A
  // hand-maintained list silently rejected "marker" the moment it was added.
  const assetIds = new Set<string>([
    ...STAMP_CATALOG.map((definition) => definition.id),
    ...Object.keys(LEGACY_STAMP_TO_MARKER),
    "text_label", "room_number",
  ]);
  const patches = candidate.geometry?.terrainPatches;
  const patchesAreValid = patches === undefined || (
    Array.isArray(patches) && patches.every((patch) =>
      patch &&
      typeof patch.id === "string" && patch.id.length > 0 &&
      isTerrainPatchType(patch.type) &&
      Array.isArray(patch.polygon) &&
      patch.polygon.length > 0 &&
      patch.polygon.every((ring) => Array.isArray(ring) && ring.length >= 4 && ring.every(isNumberPair))
    )
  );
  const hexCellsAreValid = hexCells === undefined || (
    Array.isArray(hexCells) && hexCells.every((cell) =>
      Number.isInteger(cell.q) && Number.isInteger(cell.r) && terrainIds.has(cell.terrain),
    )
  );
  // Additive and optional, like wallType before it (D-019): absent on every
  // pre-Slice-A document, and strictly validated whenever it is present. A key
  // whose binding id matches no object is an orphan, which is legal.
  const roomKeysAreValid = candidate.roomKeys === undefined || isRoomKeyMap(candidate.roomKeys);
  const objectsAreValid = Array.isArray(candidate.objects) && candidate.objects.every((object) =>
    object &&
    typeof object.id === "string" && object.id.length > 0 &&
    assetIds.has(object.assetId) &&
    Number.isFinite(object.x) && Number.isFinite(object.y) &&
    Number.isFinite(object.width) && object.width > 0 &&
    Number.isFinite(object.height) && object.height > 0 &&
    Number.isFinite(object.rotation) && Number.isFinite(object.zIndex) &&
    (object.mode === undefined || object.mode === "square" || object.mode === "hex") &&
    (object.wallType === undefined || isWallObjectType(object.wallType)) &&
    (object.floorType === undefined || isFloorObjectType(object.floorType)) &&
    (object.markerType === undefined || isMarkerObjectType(object.markerType)) &&
    (object.text === undefined || typeof object.text === "string") &&
    (object.fontSize === undefined || (Number.isFinite(object.fontSize) && object.fontSize > 0))
  );

  if (
    candidate.version !== "1.0.0" ||
    !candidate.meta ||
    typeof candidate.meta.title !== "string" ||
    !["square", "hex"].includes(candidate.meta.gridType) ||
    !Number.isFinite(candidate.meta.gridSizeWorld) || candidate.meta.gridSizeWorld <= 0 ||
    (candidate.meta.dungeonGridSizeWorld !== undefined && (!Number.isFinite(candidate.meta.dungeonGridSizeWorld) || candidate.meta.dungeonGridSizeWorld <= 0)) ||
    (candidate.meta.hexGridSizeWorld !== undefined && (!Number.isFinite(candidate.meta.hexGridSizeWorld) || candidate.meta.hexGridSizeWorld <= 0)) ||
    !Number.isFinite(candidate.meta.printGridSizeMm) || candidate.meta.printGridSizeMm <= 0 ||
    !ringsAreValid ||
    !hexCellsAreValid ||
    !patchesAreValid ||
    !roomKeysAreValid ||
    !objectsAreValid
  ) {
    throw new Error(t("validate.unsupported"));
  }

  const document = candidate as MapDocument;
  return {
    ...document,
    // Legacy stamps are rewritten here as well as in the v3 -> v4 migrator, so a
    // file behaves the same whether it arrives by import or out of storage.
    objects: assignMissingObjectModes(document.objects, document.meta.gridType)
      .map(normaliseLegacyMarker),
  };
};
